import React, { useState } from "react";
import DOMPurify from "dompurify";
import { TechnicalDocumentationSectionStatus as SS } from "../Shared/Constants.js";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";

/*
 * Shared display helpers for the eTD screens.
 */

const SECTION_STATUS = {
    [SS.Unchanged]: { label: "Unchanged", dot: "#c9ced4", pillBg: "#eef0f2", pillFg: "#5a6472" },
    [SS.Draft]: { label: "Draft", dot: "#8ab4e8", pillBg: "#e8f0fb", pillFg: "#2b6cb0" },
    [SS.Pending]: { label: "Pending review", dot: "#f7b204", pillBg: "#fef4dc", pillFg: "#8a6a12" },
    [SS.Rejected]: { label: "Rejected", dot: "#d1153a", pillBg: "#fde8ec", pillFg: "#a3132f" },
    [SS.Approved]: { label: "Approved", dot: "#48ca94", pillBg: "#e3f7ee", pillFg: "#1f7a52" },
};

const sectionStatusMeta = (status) =>
    SECTION_STATUS[status] || SECTION_STATUS[SS.Unchanged];

// _SectionVersion.State: 0 Draft, 1 Approved, 2 Rejected -- the real per-section state.
// SECTION_STATUS above is the legacy _Content.ReviewStatus, still used where
// GET Sections returns s.Status.
const SV_STATE = {
    0: { label: "Draft", dot: "#f7b204", pillBg: "#fef4dc", pillFg: "#8a6a12" },
    1: { label: "Approved", dot: "#48ca94", pillBg: "#e3f7ee", pillFg: "#1f7a52" },
    2: { label: "Rejected", dot: "#d1153a", pillBg: "#fde8ec", pillFg: "#a3132f" },
};
const svStateMeta = (state) => SV_STATE[state] || SV_STATE[0];


const SectionStatusDot = ({ status, title }) => {
    const meta = sectionStatusMeta(status);
    return (
        <span
            className="td-dot"
            style={{ background: meta.dot }}
            title={title || meta.label}
        />
    );
};


const SectionStatusPill = ({ status, label }) => {
    const meta = sectionStatusMeta(status);
    return (
        <span
            className="td-status-pill"
            style={{ background: meta.pillBg, color: meta.pillFg }}
        >
            {label || meta.label}
        </span>
    );
};


const SectionVersionDot = ({ state, title }) => {
    const meta = svStateMeta(state);
    return <span className="td-dot" style={{ background: meta.dot }} title={title || meta.label} />;
};

const SectionVersionPill = ({ state, label }) => {
    const meta = svStateMeta(state);
    return (
        <span className="td-status-pill" style={{ background: meta.pillBg, color: meta.pillFg }}>
            {label || meta.label}
        </span>
    );
};

// Document version state is just the Published bit: Draft or Approved. Its own palette, independent
// of SV_STATE (section status), since a document being drafted isn't the same signal as a section
// still being drafted within an otherwise-approved document.
const DOC_VERSION_STATE = {
    draft: { pillBg: "#e8f0fb", pillFg: "#2b6cb0" },
    approved: SV_STATE[1],
};
const VersionStatePill = ({ published }) => {
    const meta = published ? DOC_VERSION_STATE.approved : DOC_VERSION_STATE.draft;
    return (
        <span className="td-status-pill" style={{ background: meta.pillBg, color: meta.pillFg }}>
            {published ? "Approved" : "Draft"}
        </span>
    );
};


// Streams the PDF via fetch (rather than a plain navigation) so a spinner can cover
// the download's duration; an ObjectURL + synthetic click then trigger the save.
const PdfDownloadLink = ({ href, filename, label, className, style }) => {
    const [downloading, setDownloading] = useState(false);

    const onClick = (e) => {
        e.preventDefault();
        if (downloading) return;
        setDownloading(true);
        fetch(href)
            .then((res) => {
                if (!res.ok) throw new Error("Download failed");
                return res.blob();
            })
            .then((blob) => {
                const objectUrl = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = objectUrl;
                a.download = filename || "document.pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(objectUrl);
            })
            .catch(() => {})
            .finally(() => setDownloading(false));
    };

    return (
        <a href={href} className={className} style={style} onClick={onClick}>
            {downloading ? <LoadingSpinnerImage small /> : <i className="far fa-file-pdf" />} {label}
        </a>
    );
};


const SafeHTML = ({ html, className }) => {
    const clean = DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true } });
    return <div className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
};


const Avatar = ({ name, id, size = 26 }) => {
    const initials = (name || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("");
    // deterministic-ish tint from the id so avatars are stable per user
    const palette = ["#2b6cb0", "#4a9d6b", "#8a6fd6", "#c07a3a", "#2d7f80", "#b0567f"];
    const bg = palette[Math.abs(Number(id) || 0) % palette.length];
    return (
        <div
            className="td-avatar"
            style={{ width: size, height: size, background: bg, fontSize: size * 0.4 }}
            title={name}
        >
            {initials}
        </div>
    );
};


// "12 min ago" / "3 days ago"
const readableAge = (dateStr) => {
    if (!dateStr) return "";
    const then = new Date(dateStr).getTime();
    if (isNaN(then)) return "";
    const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
    if (secs < 60) return "just now";
    const mins = Math.round(secs / 60);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
    const months = Math.round(days / 30);
    if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
    return `${Math.round(months / 12)} year${months >= 24 ? "s" : ""} ago`;
};


const formatDate = (dateStr, includeTime = false) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const opts = { year: "numeric", month: "short", day: "2-digit" };
    let out = d.toLocaleDateString("en-GB", opts);
    if (includeTime) {
        out += " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    }
    return out;
};


const userName = (users, id) => users?.find((u) => u.Id == id)?.Name || "";


// Compute the "1", "1.1", "3.1.2" outline number for every section from the
// ParentId / Order tree. Returns a { [sectionId]: "1.2.1" } map.
const buildSectionNumbers = (sections) => {
    const byParent = {};
    (sections || []).forEach((s) => {
        const p = s.ParentId ?? "root";
        (byParent[p] = byParent[p] || []).push(s);
    });
    Object.values(byParent).forEach((list) =>
        list.sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
    );
    const numbers = {};
    const walk = (parentKey, prefix) => {
        (byParent[parentKey] || []).forEach((s, i) => {
            const num = prefix ? `${prefix}.${i + 1}` : `${i + 1}`;
            numbers[s.Id] = num;
            walk(s.Id, num);
        });
    };
    walk("root", "");
    return numbers;
};


// depth of a section in the tree (0 = top level) for indentation
const sectionDepth = (sections, section) => {
    let depth = 0;
    let cur = section;
    const byId = {};
    (sections || []).forEach((s) => (byId[s.Id] = s));
    while (cur && cur.ParentId != null && byId[cur.ParentId]) {
        depth++;
        cur = byId[cur.ParentId];
        if (depth > 20) break;
    }
    return depth;
};


// "12.4 KB" / "3.1 MB"
const fileSize = (bytes) => {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};


export {
    SECTION_STATUS,
    SV_STATE,
    sectionStatusMeta,
    svStateMeta,
    SectionStatusDot,
    SectionStatusPill,
    SectionVersionDot,
    SectionVersionPill,
    VersionStatePill,
    PdfDownloadLink,
    SafeHTML,
    Avatar,
    readableAge,
    formatDate,
    fileSize,
    userName,
    buildSectionNumbers,
    sectionDepth,
};
